import type { ParentConfig } from "@hooks/useParentCommunication";
import { getDevelopmentConfig } from "@utilities/appUtils";
import { useState } from "react";

interface DevConfigPopupProps {
  onSubmit: (config: ParentConfig) => void;
}

export const DevConfigPopup: React.FC<DevConfigPopupProps> = ({ onSubmit }) => {
  const defaultConfig = getDevelopmentConfig();
  
  const [config, setConfig] = useState<ParentConfig>(defaultConfig);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(config);
  };

  const handleChange = (field: keyof ParentConfig, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="p-6">
          <h2 className="mb-4 text-2xl font-bold text-gray-800">
            Development Configuration
          </h2>
          <p className="mb-6 text-sm text-gray-600">
            This popup only appears in standalone mode (not in iframe). Configure the development settings below.
          </p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="userId">
                User ID
              </label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="userId"
                onChange={(e) => handleChange("userId", e.target.value)}
                required
                type="text"
                value={config.userId}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="clientId">
                Client ID
              </label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="clientId"
                onChange={(e) => handleChange("clientId", e.target.value)}
                required
                type="text"
                value={config.clientId}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="appId">
                App ID
              </label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="appId"
                onChange={(e) => handleChange("appId", e.target.value)}
                required
                type="text"
                value={config.appId}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="language">
                Language
              </label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="language"
                onChange={(e) => handleChange("language", e.target.value)}
                value={config.language || "en"}
              >
                <option value="en">English</option>
                <option value="ar">Arabic</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="jwt">
                JWT Token
              </label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                id="jwt"
                onChange={(e) => handleChange("jwt", e.target.value)}
                required
                value={config.jwt}
              />
              <p className="mt-1 text-xs text-gray-500">
                Paste your JWT token here
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                className="flex-1 rounded-md bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                type="submit"
              >
                Apply Configuration
              </button>
              <button
                className="rounded-md border border-gray-300 px-4 py-2 font-medium transition-colors hover:bg-gray-50"
                onClick={() => setConfig(defaultConfig)}
                type="button"
              >
                Reset to Default
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
